import { useLoanDashboard } from "@/hooks/useLoans";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Loader, DollarSign, TrendingUp, Clock, CheckCircle2, XCircle,
  AlertTriangle, CreditCard, Wallet, BarChart3, Calendar, Users,
  FileText, AlertCircle, ArrowUpRight, ArrowDownRight, Banknote,
  CircleDollarSign, HandCoins, Ban, Percent,
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { cn } from "@/lib/utils";

export default function LoanDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useLoanDashboard();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  if (!data) return null;

  const { summary, financial, monthly, approvalProgress, monthlyTrend, overdueLoans } = data;

  const kpiCards = [
    {
      title: "Total Loan Portfolio",
      value: formatCurrency(financial.totalPortfolio),
      icon: <DollarSign className="h-5 w-5 text-blue-600" />,
      color: "bg-blue-50",
      textColor: "text-blue-700",
      onClick: () => navigate("/loans"),
    },
    {
      title: "Active Loans",
      value: summary.activeLoans,
      icon: <CreditCard className="h-5 w-5 text-green-600" />,
      color: "bg-green-50",
      textColor: "text-green-700",
      onClick: () => navigate("/loans"),
    },
    {
      title: "Pending Approvals",
      value: summary.pendingApprovals,
      icon: <Clock className="h-5 w-5 text-amber-600" />,
      color: "bg-amber-50",
      textColor: "text-amber-700",
      onClick: () => navigate("/loans"),
    },
    {
      title: "Ready for Disbursement",
      value: summary.readyForDisbursement,
      icon: <Banknote className="h-5 w-5 text-indigo-600" />,
      color: "bg-indigo-50",
      textColor: "text-indigo-700",
      onClick: () => navigate("/loans"),
    },
    {
      title: "Due This Month",
      value: formatCurrency(monthly.dueThisMonth),
      icon: <Calendar className="h-5 w-5 text-purple-600" />,
      color: "bg-purple-50",
      textColor: "text-purple-700",
      onClick: () => navigate("/loans/monthly-repayments"),
    },
    {
      title: "Paid This Month",
      value: formatCurrency(monthly.paidThisMonth),
      icon: <HandCoins className="h-5 w-5 text-emerald-600" />,
      color: "bg-emerald-50",
      textColor: "text-emerald-700",
      onClick: () => navigate("/loans/monthly-repayments?tab=paid"),
    },
    {
      title: "Missed Payments",
      value: monthly.missedPayments,
      icon: <Ban className="h-5 w-5 text-red-600" />,
      color: "bg-red-50",
      textColor: "text-red-700",
      onClick: () => navigate("/loans/monthly-repayments?tab=overdue"),
    },
    {
      title: "Overdue Amount",
      value: formatCurrency(monthly.overdueAmount),
      icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
      color: "bg-red-50",
      textColor: "text-red-700",
      onClick: () => navigate("/loans/monthly-repayments?tab=overdue"),
    },
    {
      title: "Expected Interest",
      value: formatCurrency(monthly.expectedInterest),
      icon: <Percent className="h-5 w-5 text-cyan-600" />,
      color: "bg-cyan-50",
      textColor: "text-cyan-700",
    },
    {
      title: "Collection Rate",
      value: `${financial.collectionRate}%`,
      icon: <TrendingUp className="h-5 w-5 text-[#009A44]" />,
      color: "bg-green-50",
      textColor: "text-[#009A44]",
    },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpiCards.map((card, i) => (
          <Card
            key={i}
            className={cn(
              "cursor-pointer hover:shadow-md transition-shadow",
              card.color
            )}
            onClick={card.onClick}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-600">{card.title}</span>
                {card.icon}
              </div>
              <p className={cn("text-xl font-bold", card.textColor)}>
                {card.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Financial Summary + Approval Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Financial Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Disbursed</span>
              <span className="font-semibold text-green-700">{formatCurrency(financial.totalPortfolio)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Repaid</span>
              <span className="font-semibold text-blue-700">{formatCurrency(financial.totalRepaid)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Outstanding Balance</span>
              <span className="font-semibold text-amber-700">{formatCurrency(financial.totalOutstanding)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Interest Income</span>
              <span className="font-semibold text-purple-700">{formatCurrency(financial.totalInterestIncome)}</span>
            </div>
            <div className="pt-2 border-t">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm font-medium">Collection Rate</span>
                <span className="text-sm font-bold text-[#009A44]">{financial.collectionRate}%</span>
              </div>
              <Progress value={financial.collectionRate} className="h-2" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Approval Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">No Approvals (0/3)</span>
              <Badge variant="secondary" className="bg-gray-100">{approvalProgress.noApprovals}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">One Approval (1/3)</span>
              <Badge variant="secondary" className="bg-amber-100 text-amber-700">{approvalProgress.oneApproval}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Two Approvals (2/3)</span>
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">{approvalProgress.twoApprovals}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Fully Approved (3/3)</span>
              <Badge className="bg-green-600 text-white">{approvalProgress.fullyApproved}</Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend Chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Monthly Trend (Last 6 Months)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {monthlyTrend?.map((trend: any, i: number) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-xs text-gray-500 w-16">{trend.month}</span>
                <div className="flex-1 flex items-center gap-2">
                  <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden flex">
                    <div
                      className="h-full bg-green-500 rounded-l-full"
                      style={{ width: `${Math.min(100, (trend.disbursed / (financial.totalPortfolio || 1)) * 100)}%` }}
                    />
                    <div
                      className="h-full bg-blue-500"
                      style={{ width: `${Math.min(100, (trend.repaid / (financial.totalRepaid || 1)) * 100)}%` }}
                    />
                  </div>
                  <span className="text-xs text-gray-600 w-20 text-right">{trend.applications} apps</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Overdue Loans */}
      {overdueLoans?.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              Overdue Loans ({overdueLoans.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {overdueLoans.map((loan: any, i: number) => (
                <div key={i} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                  <div>
                    <span className="text-sm font-medium">{loan.memberName}</span>
                    <span className="text-xs text-gray-500 ml-2">{loan.loanCode}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-red-700">{formatCurrency(loan.scheduleDue || loan.dueAmount)}</span>
                    <Badge variant="destructive" className="text-[10px]">{loan.daysOverdue}d overdue</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="bg-[#009A44] hover:bg-[#008238] text-white" onClick={() => navigate("/loans/apply")}>
          <FileText className="h-3.5 w-3.5 mr-1.5" />
          New Loan
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate("/loans/monthly-repayments")}>
          <Calendar className="h-3.5 w-3.5 mr-1.5" />
          Monthly Repayments
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate("/loans/reports")}>
          <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
          Reports
        </Button>
      </div>
    </div>
  );
}
